import React, { createContext, useContext, useState, useEffect } from 'react';
import './dashboard/assessment.css';

// --- Shared UI: edit icon ---

/** Outlined pencil icon used for edit actions across the assessment module. */
export const AssessmentEditIcon = ({ className = 'w-4 h-4', title }) => (
  <svg
    className={`assessment-edit-icon shrink-0 ${className}`}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden={title ? undefined : true}
    role={title ? 'img' : undefined}
  >
    {title ? <title>{title}</title> : null}
    <path
      d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// --- Shared UI: icon-only tooltips ---

const ASSESSMENT_TOOLTIP_ROOT =
  '.assess-mod-page, .results-evaluation-container, .hr-upload-page, .assess-mod-student-exec, .assess-mod-student-page';

const FLOATER_ID = 'assess-mod-tooltip-floater';

const hasVisibleLabelText = (el) => {
  if (!el) return false;
  for (const node of el.childNodes) {
    if (node.nodeType === Node.TEXT_NODE && String(node.textContent || '').trim()) {
      return true;
    }
  }
  const visibleSpan = el.querySelector('span:not(.sr-only)');
  if (visibleSpan && String(visibleSpan.textContent || '').trim()) {
    return true;
  }
  return String(el.textContent || '').replace(/\s+/g, ' ').trim().length > 0;
};

/** Tooltips only for icon-only buttons/links (not text labels like "Next" or "Export"). */
export const isIconOnlyControl = (el) => {
  if (!el) return false;
  if (el.hasAttribute('data-assess-tip-icon')) return true;
  if (!el.matches('button, a, [role="button"]')) return false;
  if (hasVisibleLabelText(el)) return false;
  return Boolean(
    el.querySelector('svg, i[class*="bi"], .assessment-edit-icon, img[alt], [class*="react-icons"]')
  );
};

export const resolveAssessmentTipText = (el) => {
  if (!el || el.getAttribute('data-assess-tip') === '') return null;
  if (!isIconOnlyControl(el)) return null;

  const explicit = el.getAttribute('data-assess-tip');
  if (explicit) return explicit.trim();

  const title = el.getAttribute('title');
  if (title) {
    el.setAttribute('data-assess-tip', title);
    el.removeAttribute('title');
    return title.trim();
  }

  const aria = el.getAttribute('aria-label');
  if (aria) return aria.trim();

  return null;
};

const isInsideAssessmentModule = (el) => Boolean(el?.closest(ASSESSMENT_TOOLTIP_ROOT));

const positionFloater = (floater, target) => {
  const rect = target.getBoundingClientRect();
  const gap = 6;
  const pos = target.getAttribute('data-assess-tip-pos') || 'bottom';

  floater.style.display = 'block';
  floater.classList.remove('is-visible');

  const fw = floater.offsetWidth;
  const fh = floater.offsetHeight;

  let top;
  let left;

  switch (pos) {
    case 'top':
      top = rect.top - fh - gap;
      left = rect.left;
      break;
    case 'left':
      top = rect.top;
      left = rect.left - fw - gap;
      break;
    case 'right':
      top = rect.top;
      left = rect.right + gap;
      break;
    default:
      top = rect.bottom + gap;
      left = rect.left;
  }

  left = Math.max(8, Math.min(left, window.innerWidth - fw - 8));
  top = Math.max(8, Math.min(top, window.innerHeight - fh - 8));

  floater.style.left = `${left}px`;
  floater.style.top = `${top}px`;
  floater.classList.add('is-visible');
};

/** Props for icon-only controls (styled tooltip, not native browser title). */
export const assessTipProps = (tip, position = 'bottom') => {
  if (!tip) return {};
  return {
    'data-assess-tip': tip,
    'data-assess-tip-icon': '',
    'data-assess-tip-pos': position,
    'aria-label': tip,
  };
};

/** Module-wide tooltips for icon-only actions in the assessment module. */
export const useAssessmentTooltips = () => {
  useEffect(() => {
    let floater = document.getElementById(FLOATER_ID);
    if (!floater) {
      floater = document.createElement('div');
      floater.id = FLOATER_ID;
      floater.className = 'assess-mod-tooltip-floater';
      floater.setAttribute('role', 'tooltip');
      document.body.appendChild(floater);
    }

    let activeTarget = null;

    const hide = () => {
      activeTarget = null;
      floater.classList.remove('is-visible');
      floater.style.display = 'none';
      floater.textContent = '';
    };

    const show = (target) => {
      const tip = resolveAssessmentTipText(target);
      if (!tip) {
        hide();
        return;
      }
      activeTarget = target;
      floater.textContent = tip;
      positionFloater(floater, target);
    };

    const findTipTarget = (node) => {
      let el = node instanceof Element ? node : null;
      while (el) {
        if (!isInsideAssessmentModule(el)) return null;
        if (isIconOnlyControl(el) && resolveAssessmentTipText(el)) return el;
        if (el.matches(ASSESSMENT_TOOLTIP_ROOT)) break;
        el = el.parentElement;
      }
      return null;
    };

    const onPointerOver = (e) => {
      const target = findTipTarget(e.target);
      if (!target) return;
      if (activeTarget === target) return;
      show(target);
    };

    const onPointerOut = (e) => {
      if (!activeTarget) return;
      const related = e.relatedTarget;
      if (related && activeTarget.contains(related)) return;
      if (related === floater) return;
      const next = findTipTarget(related);
      if (next === activeTarget) return;
      hide();
    };

    const onFocusIn = (e) => {
      const target = findTipTarget(e.target);
      if (target) show(target);
    };

    const onFocusOut = (e) => {
      if (!activeTarget) return;
      const related = e.relatedTarget;
      if (related && activeTarget.contains(related)) return;
      hide();
    };

    const onScroll = () => {
      if (activeTarget) positionFloater(floater, activeTarget);
    };

    document.addEventListener('mouseover', onPointerOver, true);
    document.addEventListener('mouseout', onPointerOut, true);
    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', onFocusOut, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);

    return () => {
      document.removeEventListener('mouseover', onPointerOver, true);
      document.removeEventListener('mouseout', onPointerOut, true);
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('focusout', onFocusOut, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      hide();
    };
  }, []);
};

// --- Module navigation context ---

const AssessmentContext = createContext();

export const AssessmentProvider = ({ children }) => {
  const [activeModule, setActiveModuleState] = useState(() => {
    return localStorage.getItem('assessment_active_module') || 'inventory';
  });
  const [moduleReturnTo, setModuleReturnToState] = useState(() => {
    return localStorage.getItem('assessment_module_return_to') || null;
  });
  const [selectedTestId, setSelectedTestIdState] = useState(() => {
    return localStorage.getItem('assessment_selected_test_id') || null;
  });

  const setActiveModule = (module) => {
    setActiveModuleState(module);
    localStorage.setItem('assessment_active_module', module);
  };

  const setModuleReturnTo = (module) => {
    setModuleReturnToState(module);
    if (module) {
      localStorage.setItem('assessment_module_return_to', module);
    } else {
      localStorage.removeItem('assessment_module_return_to');
    }
  };

  const navigateToModule = (module, returnTo = null) => {
    if (returnTo) {
      setModuleReturnTo(returnTo);
    }
    setActiveModule(module);
  };

  const returnFromModule = () => {
    const target = moduleReturnTo || 'inventory';
    setModuleReturnTo(null);
    setActiveModule(target);
  };

  const setSelectedTestId = (id) => {
    setSelectedTestIdState(id);
    if (id) {
      localStorage.setItem('assessment_selected_test_id', id);
    } else {
      localStorage.removeItem('assessment_selected_test_id');
    }
  };

  return (
    <AssessmentContext.Provider
      value={{
        activeModule,
        setActiveModule,
        moduleReturnTo,
        setModuleReturnTo,
        navigateToModule,
        returnFromModule,
        selectedTestId,
        setSelectedTestId,
      }}
    >
      {children}
    </AssessmentContext.Provider>
  );
};

export const useAssessmentContext = () => useContext(AssessmentContext);
